import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRoute } from "@react-navigation/native";
import { useMutation } from "@apollo/client";
import { Formik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";

import Container from "../../components/Container";
import Header from "../../components/Header";
import Text from "../../components/Text";
import Button from "../../components/Button";
import TextInputField from "../../components/TextInputField";
import StickyFooter from "../../components/StickyFooter";
import { colors, fonts } from "../../theme";
import { CREATE_PUBLIC_APPOINTMENT } from "./gql";

const Schema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Enter a valid email").required("Email is required"),
  phone: Yup.string().required("Phone number is required"),
});

const styles = StyleSheet.create({
  container: { backgroundColor: colors.black, flex: 1 },
  scrollView: { paddingHorizontal: 20 },
  summaryCard: {
    backgroundColor: "#262b42",
    borderWidth: 1.5,
    borderColor: "#2a2d3f",
    borderRadius: 14,
    padding: 14,
    marginTop: 20,
    marginBottom: 10,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 7,
  },
  summaryRow__divider: {
    borderTopWidth: 1,
    borderTopColor: "#2a2d3f",
  },
  summaryKey: { color: "#6b6f82", fontSize: 12 },
  summaryValue: {
    color: colors.white,
    fontFamily: fonts.semiBold,
    fontSize: 12,
  },
  sectionLabel: {
    fontFamily: "RobotoMono_400Regular",
    color: colors.midGrey,
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: 1,
    marginTop: 22,
    marginBottom: 10,
  },
  formError: { color: colors.red, marginTop: 10 },
});

type Props = { navigation: any };

const ContactInfoScreen: React.FC<Props> = ({ navigation }) => {
  const { params } = useRoute<any>();
  const { store, service, employeeId, startsAt, durationMinutes } = params || {};
  const [createAppointment] = useMutation(CREATE_PUBLIC_APPOINTMENT);

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Formik
        initialValues={{ firstName: "", lastName: "", email: "", phone: "" }}
        validationSchema={Schema}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          setStatus(undefined);
          try {
            const response = await createAppointment({
              variables: {
                input: {
                  storeId: store.id,
                  serviceId: service.id,
                  employeeId,
                  startsAt,
                  durationMinutes,
                  customer: values,
                },
              },
            });

            const errors = response.data?.result?.errors;
            if (errors?.length) {
              setStatus(errors[0].message);
              setSubmitting(false);
              return;
            }

            navigation.navigate("BookingConfirmation", {
              store,
              service,
              startsAt,
              customerFirstName: values.firstName,
              visitId: response.data?.result?.visit?.id,
            });
          } catch (err) {
            setStatus("Something went wrong. Please try again.");
            setSubmitting(false);
          }
        }}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          errors,
          touched,
          status,
          isSubmitting,
          isValid,
        }) => (
          <>
            <Container>
              <ScrollView
                keyboardShouldPersistTaps="handled"
                style={styles.scrollView}
              >
                <Header
                  title="Almost there"
                  description="We'll text and email your confirmation."
                />

                <View style={styles.summaryCard}>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryKey}>Service</Text>
                    <Text style={styles.summaryValue}>{service?.name}</Text>
                  </View>
                  <View style={[styles.summaryRow, styles.summaryRow__divider]}>
                    <Text style={styles.summaryKey}>When</Text>
                    <Text style={styles.summaryValue}>
                      {startsAt ? format(new Date(startsAt), "EEE, MMM d 'at' h:mm a") : ""}
                    </Text>
                  </View>
                </View>

                <Text style={styles.sectionLabel}>Your info</Text>
                <TextInputField
                  name="firstName"
                  placeholder="First name"
                  textContentType="givenName"
                  hasError={!!errors.firstName && touched.firstName}
                  onChangeText={handleChange("firstName")}
                  onBlur={handleBlur("firstName")}
                  value={values.firstName}
                  touched={!!touched.firstName}
                  error={errors.firstName}
                />
                <TextInputField
                  name="lastName"
                  placeholder="Last name"
                  textContentType="familyName"
                  hasError={!!errors.lastName && touched.lastName}
                  onChangeText={handleChange("lastName")}
                  onBlur={handleBlur("lastName")}
                  value={values.lastName}
                  touched={!!touched.lastName}
                  error={errors.lastName}
                />
                <TextInputField
                  name="email"
                  placeholder="Email"
                  textContentType="emailAddress"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  hasError={!!errors.email && touched.email}
                  onChangeText={handleChange("email")}
                  onBlur={handleBlur("email")}
                  value={values.email}
                  touched={!!touched.email}
                  error={errors.email}
                />
                <TextInputField
                  name="phone"
                  placeholder="Phone"
                  textContentType="telephoneNumber"
                  keyboardType="phone-pad"
                  hasError={!!errors.phone && touched.phone}
                  onChangeText={handleChange("phone")}
                  onBlur={handleBlur("phone")}
                  value={values.phone}
                  touched={!!touched.phone}
                  error={errors.phone}
                />
                {!!status && <Text style={styles.formError}>{status}</Text>}
              </ScrollView>
            </Container>
            <StickyFooter>
              <Button
                title="Confirm booking"
                onPress={handleSubmit}
                disabled={!isValid}
                isLoading={isSubmitting}
              />
            </StickyFooter>
          </>
        )}
      </Formik>
    </SafeAreaView>
  );
};

export default ContactInfoScreen;
